export interface StockAdjustmentLineDto {
  id: string;
  materialId: string;
  materialCode: string;
  materialName: string;
  locationId: string | null;
  locationCode: string | null;
  batchId: string | null;
  batchNo: string | null;
  currentQty: number;
  adjustedQty: number;
  remarks: string | null;
  // Null unless 3PL is enabled — auto-resolved from the existing on-hand row this line adjusts.
  clientId?: string | null;
  clientName?: string | null;
  /** The label this line was scanned against, echoed back so editing a draft can re-send the same
   *  scan. Both null on a row-wise line (Barcode Required off). */
  scannedBarcode?: string | null;
  labelSource?: 'Grn' | 'Production' | 'JobWork' | null;
}

export interface StockAdjustmentLineInput {
  materialId: string;
  locationId: string | null;
  batchId: string | null;
  adjustedQty: number;
  remarks: string | null;
  /** Required when Process Configuration's barcodeMandatory is on — the server re-resolves it to
   *  a label and takes material/location/batch from the label, ignoring the fields above. */
  scannedBarcode?: string | null;
}

export interface StockAdjustmentDto {
  id: string;
  adjustmentNumber: string;
  warehouseId: string;
  warehouseName: string;
  adjustmentDate: string;
  reason: string;
  status: string;
  lines: StockAdjustmentLineDto[];
}

export interface CreateStockAdjustmentRequest {
  warehouseId: string;
  reason: string;
  lines: StockAdjustmentLineInput[];
}

/** Only usable while Status is still "Draft" — see StockAdjustmentService.UpdateAsync. */
export interface UpdateStockAdjustmentRequest {
  warehouseId: string;
  reason: string;
  lines: StockAdjustmentLineInput[];
}

export interface StockTransferLineDto {
  id: string;
  materialId: string;
  materialCode: string;
  materialName: string;
  qty: number;
  fromLocationId: string | null;
  fromLocationCode: string | null;
  toLocationId: string | null;
  toLocationCode: string | null;
  batchId: string | null;
  batchNo: string | null;
  clientId?: string | null;
  clientName?: string | null;
}

export interface StockTransferLineInput {
  materialId: string;
  qty: number;
  fromLocationId: string | null;
  toLocationId: string | null;
  batchId: string | null;
  /** Required when barcodeMandatory is on - the server re-resolves it and takes material,
   *  from-location, batch and quantity from the label. */
  scannedBarcode?: string | null;
}

export interface StockTransferDto {
  id: string;
  transferNumber: string;
  fromWarehouseId: string;
  fromWarehouseName: string;
  toWarehouseId: string;
  toWarehouseName: string;
  transferDate: string;
  status: string;
  lines: StockTransferLineDto[];
}

export interface CreateStockTransferRequest {
  fromWarehouseId: string;
  toWarehouseId: string;
  lines: StockTransferLineInput[];
}

/** Only usable while Status is still "Draft" — see StockTransferService.UpdateAsync. */
export interface UpdateStockTransferRequest {
  fromWarehouseId: string;
  toWarehouseId: string;
  lines: StockTransferLineInput[];
}

export interface CycleCountLineDto {
  id: string;
  materialId: string;
  materialCode: string;
  materialName: string;
  locationId: string | null;
  locationCode: string | null;
  batchId: string | null;
  batchNo: string | null;
  systemQty: number;
  countedQty: number | null;
  clientId?: string | null;
  clientName?: string | null;
}

export interface CycleCountLineInput {
  materialId: string;
  locationId: string | null;
  batchId: string | null;
}

export interface RecordCountsLineInput {
  cycleCountLineId: string;
  countedQty: number;
}

export interface RecordCountsRequest {
  lines: RecordCountsLineInput[];
}

export interface CycleCountDto {
  id: string;
  cycleCountNumber: string;
  warehouseId: string;
  warehouseName: string;
  countDate: string;
  status: string;
  lines: CycleCountLineDto[];
}

export interface CreateCycleCountRequest {
  warehouseId: string;
  lines: CycleCountLineInput[];
}

/** Only usable while Status is still "Draft" — see CycleCountService.UpdateAsync. */
export interface UpdateCycleCountRequest {
  warehouseId: string;
  lines: CycleCountLineInput[];
}

export interface ReturnNoteLineDto {
  id: string;
  materialId: string;
  materialCode: string;
  materialName: string;
  qty: number;
  reason: string | null;
  batchId: string | null;
  batchNo: string | null;
  clientId?: string | null;
  clientName?: string | null;
}

/** clientId is only consulted for a "FromCustomer" return of a new/unbatched item (no existing
 * on-hand row to resolve ownership from) — ignored otherwise (resolved server-side instead). */
export interface ReturnNoteLineInput {
  materialId: string;
  qty: number;
  reason: string | null;
  clientId?: string | null;
}

export interface ReturnNoteDto {
  id: string;
  returnNumber: string;
  warehouseId: string;
  warehouseName: string;
  returnType: string;
  customerId: string | null;
  customerName: string | null;
  supplierId: string | null;
  supplierName: string | null;
  returnDate: string;
  status: string;
  lines: ReturnNoteLineDto[];
}

export interface CreateReturnNoteRequest {
  warehouseId: string;
  returnType: string;
  customerId: string | null;
  supplierId: string | null;
  lines: ReturnNoteLineInput[];
}

/** Only usable while Status is still "Draft" — see ReturnNoteService.UpdateAsync. */
export interface UpdateReturnNoteRequest {
  warehouseId: string;
  returnType: string;
  customerId: string | null;
  supplierId: string | null;
  lines: ReturnNoteLineInput[];
}

/** Mirrors Shared.Constants.StockStatus — the four conditions a quantity can be held in. */
export const STOCK_STATUSES = ['Available', 'Quarantine', 'Blocked', 'Damaged'] as const;
export type StockStatus = (typeof STOCK_STATUSES)[number];

/** One material/location/batch/status combination currently holding stock. `movableQty` is what a
 *  status change may actually take — on hand less anything reserved for open pick lists. */
export interface StockStatusBalanceDto {
  materialId: string;
  materialCode: string;
  materialName: string;
  warehouseId: string;
  warehouseName: string;
  locationId: string | null;
  locationCode: string | null;
  batchId: string | null;
  batchNo: string | null;
  clientId: string | null;
  stockStatus: string;
  quantityOnHand: number;
  quantityAllocated: number;
  movableQty: number;
}

export interface StockStatusChangeRequest {
  materialId: string;
  warehouseId: string;
  locationId: string | null;
  batchId: string | null;
  clientId: string | null;
  fromStatus: string;
  toStatus: string;
  qty: number;
  remarks: string | null;
  /** Required when barcodeMandatory is on: the exact physical units being reclassified. Quantity
   *  is then the sum of these labels' own quantities, computed server-side; the qty field is ignored. */
  barcodes?: string[] | null;
}

/** One physical unit currently sitting in a given status at a given stock row — the rows the
 *  Change Stock Status picker ticks when Barcode Required is on. */
export interface StockStatusLabelDto {
  barcode: string;
  source: 'Grn' | 'Production' | 'JobWork';
  qty: number;
  batchNo: string | null;
  locationCode: string | null;
}

/** One physical unit of a material in a warehouse, as the Stock Adjustment screen lists them once a
 *  material is chosen under Barcode Required. Includes units that are NOT currently on the books —
 *  a dispatched or never-put-away label — because those are exactly what a 'Found' adjustment
 *  records. The `onHand` flag tells the two apart. */
export interface MaterialUnitDto {
  barcode: string;
  source: 'Grn' | 'Production' | 'JobWork';
  qty: number;
  locationId: string | null;
  locationCode: string | null;
  batchId: string | null;
  batchNo: string | null;
  labelStatus: string;
  stockStatus: string;
  onHand: boolean;
}

/** The units of a material plus what the stock records say is there. The two can disagree — labels
 *  are only retired by Dispatch in this build — and seeing the gap is the point of showing both. */
export interface MaterialUnitsDto {
  quantityOnHand: number;
  quantityAllocated: number;
  quantityAvailable: number;
  /** Total quantity of the on-hand units actually listed — capped at quantityOnHand. */
  unitsOnHandQty: number;
  /** Still-InStock labels held back to make the list match the stock figure. Each is a stale
   *  record: labels are only retired by Dispatch in this build. */
  hiddenUnitCount: number;
  units: MaterialUnitDto[];
}

/** One row of what is on hand in a selected transfer source area (warehouse / zone / rack-or-bin).
 *  `availableQty` is on hand less anything reserved for an open pick list. */
export interface TransferAvailableStockDto {
  materialId: string;
  materialCode: string;
  materialName: string;
  locationId: string | null;
  locationCode: string | null;
  locationType: string | null;
  zoneId: string | null;
  zoneName: string | null;
  batchId: string | null;
  batchNo: string | null;
  clientId: string | null;
  quantityOnHand: number;
  quantityAllocated: number;
  availableQty: number;
}

/** One physical unit in the selected area — the barcode-wise transfer picker. */
export interface TransferUnitDto {
  barcode: string;
  source: 'Grn' | 'Production' | 'JobWork';
  qty: number;
  locationId: string | null;
  locationCode: string | null;
  batchId: string | null;
  batchNo: string | null;
}
